(() => {
  "use strict";

  const DEBUG =
    new URLSearchParams(window.location.search).get("divaDebug") === "1";

  const debug = (...args) => {
    if (DEBUG) console.info("[DIVA]", ...args);
  };

  const normalize = (value = "") =>
    String(value)
      .toLowerCase()
      .replace(/×/g, "x")
      .replace(/,/g, ".")
      .replace(/\s+/g, "")
      .replace(/weiß/g, "weiss")
      .replace(/grey/g, "gray")
      .replace(/creme/g, "beige")
      .replace(/cream/g, "beige")
      .replace(/[()]/g, "");

  const formatSize = (size) => {
    if (!size) return "–";
    return size.replace("x", " × ") + " m";
  };

  const SIZE_LABELS = {
    "2x4": "2 × 4 m",
    "2x5": "2 × 5 m",
    "3x5": "3 × 5 m",
    "3x6": "3 × 6 m",
    "3x7": "3 × 7 m",
    "4x8": "4 × 8 m",
    "4x9": "4 × 9 m",
    "5x10": "5 × 10 m"
  };

  const LABELS = {
    rattan: {
      white: "Rattan Weiß",
      gray: "Rattan Grau"
    },

    liner: {
      white: "Folie Weiß",
      beige: "Folie Beige"
    },

    technologyRoute: {
      individual: "Technik individuell",
      preassembled: "Fertig verrohrte Technik"
    },

    housing: {
      own: "Eigene Technikunterbringung",
      largeBox: "Technikbox Anthrazit"
    },

    pump: {
      standard: "AstralPool Sena",
      baduPrime: "Speck BADU Prime",
      aquagem: "AQUAGEM Inver Warrior 25",
      baduDelta: "BADU Delta VS",
      smartPlus: "Speck Smart Plus 10"
    },

    filter: {
      standard: "AstralPool Cantabric",
      midaApollo: "MIDA Apollo Ø630",
      cartridge: "Kartuschenfilter"
    },

    backwash: {
      yes: "Rückspülmöglichkeit vorhanden",
      no: "Keine Rückspülmöglichkeit",
      unknown: "Rückspülmöglichkeit noch unklar"
    },

    filterMaterial: {
      filterGlass: "4 Säcke Eco Filterglass Plus",
      own: "Filtermaterial selbst besorgen"
    },

    dosing: {
      none: "Keine automatische Dosierung",
      bayrol: "BAYROL Pool Relax"
    },

    preassembled: {
      techBoxSmall: "Pool-Technikbox bis 30 m³",
      techMedium: "Technikbox Medium pH/Redox",
      orbit: "ORBIT 1.3 DOS",
      combi: "Pool Combi System mit Pool Relax Chlor"
    },

    heatPump: {
      none: "Keine Wärmepumpe",
      cosma: "MIDA Cosma 13 Full Inverter",
      zodiac: "Zodiac Z650iQ"
    },

    counterCurrent: {
      no: "Keine Gegenstromanlage",
      yes: "Gegenstromanlage Dolce Vita"
    },

    hydromassage: {
      no: "Keine Hydromassage",
      yes: "Hydromassage"
    },

    ground: {
      concrete: "Betonbodenplatte",
      slabs: "Gartenplatten",
      steel: "DIVA Stahlfundament"
    },

    adjustment: {
      no: "Keine Justageelemente",
      yes: "DIVA Justageelemente"
    },

    summerCover: {
      no: "Keine Sommerabdeckung",
      yes: "Sommerabdeckung"
    },

    reel: {
      no: "Keine Aufrollvorrichtung",
      yes: "Aufrollvorrichtung"
    },

    winterCover: {
      no: "Keine Winterabdeckung",
      yes: "Winterabdeckung"
    },

    plexiglass: {
      no: "Ohne Plexiglas-Sicherheitskit",
      yes: "Plexiglas-Sicherheitskit"
    }
  };

  const REQUIRED_SELECTION_STEPS = new Set([
    "size",
    "rattan",
    "liner",
    "technologyRoute",
    "housing",
    "pump",
    "filter",
    "backwash",
    "filterMaterial",
    "dosing",
    "preassembled",
    "heatPump",
    "counterCurrent",
    "hydromassage",
    "ground",
    "adjustment",
    "summerCover",
    "reel",
    "winterCover",
    "plexiglass"
  ]);

  class DivaConfigurator {
    constructor(root) {
      this.root = root;

      this.routes = {};
      this.productCache = new Map();
      this.productData = {};
      this.productErrors = {};

      this.state = {
        size: null,
        rattan: null,
        liner: null,

        technologyRoute: null,

        housing: null,
        pump: null,
        filter: null,
        backwash: null,
        filterMaterial: null,
        dosing: null,

        preassembled: null,

        technologyDistance: "",
        heatPump: null,
        heatPumpVariantId: null,
        heatPumpDistance: "",

        counterCurrent: null,
        hydromassage: null,

        ground: null,
        adjustment: null,

        summerCover: null,
        reel: null,
        winterCover: null,

        plexiglass: null
      };

      this.currentStep = "size";
      this.currentFlow = [];

      this.priceState = {
        diva: null,
        divaResolved: false,
        divaVariant: null,
        items: {}
      };

      this.currency =
        root.dataset.currency && root.dataset.currency.length === 3
          ? root.dataset.currency
          : "EUR";

      this.heroImage =
        "https://cdn.shopify.com/s/files/1/0885/9031/9956/files/wunderschoenerAufstellpool_e970f43e-e4b5-4dda-bdaa-04baa736cc9c.jpg?v=1737399768";

      this.init();
    }

    /* ======================================================
       INITIALIZATION
       ====================================================== */

    async init() {
      this.readRoutes();
      this.bindEvents();

      this.rebuildFlow();
      this.updateMeasurements();
      this.updatePreassembledAvailability();
      this.updateCounterCurrentAvailability();
      this.updateNavigation();
      this.updatePreview();
      this.updateSummary();
      this.updateTotal();

      await this.loadProducts();

      this.resolveAllPrices();
      this.updateProductCards();
      this.populateZodiacVariants();
      this.updateMeasurements();
      this.updatePreassembledAvailability();
      this.updateCounterCurrentAvailability();
      this.updatePreview();
      this.updateSummary();
      this.updateTotal();

      debug("Configurator initialized", this);
    }

    readRoutes() {
      const node = this.root.querySelector("[data-diva-product-routes]");

      if (!node) {
        console.warn("[DIVA] Product route JSON missing.");
        return;
      }

      try {
        this.routes = JSON.parse(node.textContent.trim());
      } catch (error) {
        console.error("[DIVA] Could not parse product routes.", error);
      }
    }

    bindEvents() {
      this.root.addEventListener("click", (event) => {
        const startButton = event.target.closest("[data-start-config]");

        if (startButton) {
          this.scrollToConfigurator();
          return;
        }

        const option = event.target.closest("[data-option]");

        if (option && this.root.contains(option)) {
          event.preventDefault();

          if (option.disabled) return;

          const step = option.dataset.step;
          const value = option.dataset.value;

          if (step && value !== undefined) {
            this.selectOption(step, value, option);
          }

          return;
        }

        const previous = event.target.closest("[data-nav-prev]");

        if (previous) {
          event.preventDefault();
          this.goPrevious();
          return;
        }

        const next = event.target.closest("[data-nav-next]");

        if (next) {
          event.preventDefault();
          this.goNext();
          return;
        }

        const tab = event.target.closest("[data-tab]");

        if (tab) {
          event.preventDefault();
          this.goToCategory(tab.dataset.tab);
        }
      });

      this.root
        .querySelectorAll("[data-state-input]")
        .forEach((input) => {
          const update = () => {
            const key = input.dataset.stateInput;

            if (!key) return;

            this.state[key] = input.value;

            this.updatePreview();
            this.updateSummary();
            this.updateConfigurationPayload();
          };

          input.addEventListener("input", update);
          input.addEventListener("change", update);
        });

      const zodiacSelect = this.root.querySelector("[data-variant-select]");

      if (zodiacSelect) {
        zodiacSelect.addEventListener("change", () => {
          this.state.heatPumpVariantId =
            zodiacSelect.value || null;

          this.resolveAllPrices();
          this.updateProductCards();
          this.updatePreview();
          this.updateSummary();
          this.updateTotal();
        });
      }
    }

    scrollToConfigurator() {
      const area =
        this.root.querySelector("[data-configurator-area]") ||
        this.root;

      area.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });

      this.showStep("size");
    }

    /* ======================================================
       FLOW
       ====================================================== */

    rebuildFlow() {
      const flow = [
        "size",
        "rattan",
        "liner",
        "technologyRoute"
      ];

      if (this.state.technologyRoute === "individual") {
        flow.push(
          "housing",
          "pump",
          "filter"
        );

        if (
          this.state.filter === "standard" ||
          this.state.filter === "midaApollo"
        ) {
          flow.push("backwash");
        }

        if (this.state.filter === "standard") {
          flow.push("filterMaterial");
        }

        flow.push("dosing");
      }

      if (this.state.technologyRoute === "preassembled") {
        flow.push(
          "preassembled",
          "backwash"
        );
      }

      flow.push(
        "technologyDistance",
        "heatPump"
      );

      if (
        this.state.heatPump &&
        this.state.heatPump !== "none"
      ) {
        flow.push("heatPumpDistance");
      }

      flow.push(
        "counterCurrent",
        "hydromassage",
        "ground",
        "adjustment",
        "summerCover"
      );

      if (this.state.summerCover === "yes") {
        flow.push("reel");
      }

      flow.push(
        "winterCover",
        "plexiglass",
        "summary"
      );

      this.currentFlow = flow;

      if (!flow.includes(this.currentStep)) {
        this.currentStep = this.findClosestAvailableStep();
      }

      this.renderPanels();
      this.updateProgress();
      this.updateTabs();
    }

    findClosestAvailableStep() {
      const preferred = [
        "size",
        "rattan",
        "liner",
        "technologyRoute"
      ];

      for (const step of preferred) {
        if (this.currentFlow.includes(step)) return step;
      }

      return this.currentFlow[0] || "size";
    }

    showStep(step) {
      if (!this.currentFlow.includes(step)) return;

      this.currentStep = step;

      this.renderPanels();
      this.updateProgress();
      this.updateTabs();
      this.updateNavigation();

      const panel =
        this.root.querySelector(
          `.dx-step[data-step-panel="${step}"]`
        );

      if (panel) {
        panel.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    }

    renderPanels() {
      this.root
        .querySelectorAll(".dx-step[data-step-panel]")
        .forEach((panel) => {
          panel.hidden =
            panel.dataset.stepPanel !== this.currentStep;
        });
    }

    goNext() {
      if (!this.validateCurrentStep()) return;

      const index =
        this.currentFlow.indexOf(this.currentStep);

      if (
        index < 0 ||
        index >= this.currentFlow.length - 1
      ) {
        return;
      }

      this.showStep(this.currentFlow[index + 1]);
    }

    goPrevious() {
      const index =
        this.currentFlow.indexOf(this.currentStep);

      if (index <= 0) return;

      this.showStep(this.currentFlow[index - 1]);
    }

    validateCurrentStep() {
      const step = this.currentStep;

      if (!REQUIRED_SELECTION_STEPS.has(step)) {
        return true;
      }

      if (step === "heatPump") {
        if (!this.state.heatPump) {
          this.showValidationMessage(step);
          return false;
        }

        if (
          this.state.heatPump === "zodiac" &&
          !this.state.heatPumpVariantId
        ) {
          this.showValidationMessage(
            step,
            "Bitte wählen Sie die gewünschte Zodiac-Ausführung."
          );
          return false;
        }

        return true;
      }

      if (!this.state[step]) {
        this.showValidationMessage(step);
        return false;
      }

      return true;
    }

    showValidationMessage(
      step,
      text = "Bitte treffen Sie zuerst eine Auswahl."
    ) {
      const panel =
        this.root.querySelector(
          `[data-step-panel="${step}"]`
        );

      if (!panel) return;

      let message =
        panel.querySelector("[data-validation-message]");

      if (!message) {
        message = document.createElement("div");
        message.dataset.validationMessage = "true";
        message.className = "dx-info-strip";
        panel.appendChild(message);
      }

      message.textContent = text;

      message.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
    }

    updateNavigation() {
      const prev =
        this.root.querySelector("[data-nav-prev]");

      const next =
        this.root.querySelector("[data-nav-next]");

      const index =
        this.currentFlow.indexOf(this.currentStep);

      if (prev) {
        prev.disabled = index <= 0;
      }

      if (next) {
        if (this.currentStep === "summary") {
          next.hidden = true;
        } else {
          next.hidden = false;
          next.textContent =
            index === this.currentFlow.length - 2
              ? "Zur Übersicht"
              : "Weiter";
        }
      }
    }

    updateProgress() {
      const index =
        this.currentFlow.indexOf(this.currentStep);

      const total =
        Math.max(this.currentFlow.length, 1);

      const current =
        Math.max(index + 1, 1);

      const percentage =
        Math.min(100, (current / total) * 100);

      const text =
        this.root.querySelector("[data-progress-text]");

      const bar =
        this.root.querySelector("[data-progress-bar]");

      if (text) {
        text.textContent =
          `Schritt ${current} von ${total}`;
      }

      if (bar) {
        bar.style.width = `${percentage}%`;
      }
    }

    updateTabs() {
      const panel =
        this.root.querySelector(
          `[data-step-panel="${this.currentStep}"]`
        );

      const category =
        panel?.dataset.category || "pool";

      this.root
        .querySelectorAll("[data-tab]")
        .forEach((button) => {
          button.classList.toggle(
            "is-active",
            button.dataset.tab === category
          );
        });
    }

    goToCategory(category) {
      const categorySteps =
        this.currentFlow.filter((step) => {
          const panel =
            this.root.querySelector(
              `[data-step-panel="${step}"]`
            );

          return panel?.dataset.category === category;
        });

      if (!categorySteps.length) return;

      this.showStep(categorySteps[0]);
    }

    /* ======================================================
       OPTION SELECTION
       ====================================================== */

    selectOption(step, value, button) {
      this.state[step] = value;

      this.root
        .querySelectorAll(
          `[data-option][data-step="${step}"]`
        )
        .forEach((option) => {
          option.classList.toggle(
            "is-selected",
            option === button
          );

          option.setAttribute(
            "aria-pressed",
            option === button ? "true" : "false"
          );
        });

      this.applyDependencies(step, value);

      this.rebuildFlow();
      this.updatePreassembledAvailability();
      this.updateCounterCurrentAvailability();
      this.updateMeasurements();
      this.updateGroundDetails();
      this.updateContextualNotices();

      this.resolveAllPrices();
      this.updateProductCards();
      this.updatePreview();
      this.updateSummary();
      this.updateTotal();
      this.updateConfigurationPayload();
      this.updateNavigation();

      debug("Selection", step, value, this.state);
    }

    applyDependencies(step, value) {
      if (step === "technologyRoute") {
        if (value === "individual") {
          this.state.preassembled = null;

          this.clearSelectedUI("preassembled");
        }

        if (value === "preassembled") {
          this.state.housing = null;
          this.state.pump = null;
          this.state.filter = null;
          this.state.filterMaterial = null;
          this.state.dosing = null;

          this.clearSelectedUI("housing");
          this.clearSelectedUI("pump");
          this.clearSelectedUI("filter");
          this.clearSelectedUI("filterMaterial");
          this.clearSelectedUI("dosing");
        }

        this.state.backwash = null;
        this.clearSelectedUI("backwash");
      }

      if (step === "filter") {
        if (value === "cartridge") {
          this.state.backwash = null;
          this.state.filterMaterial = null;

          this.clearSelectedUI("backwash");
          this.clearSelectedUI("filterMaterial");
        }

        if (value === "midaApollo") {
          this.state.filterMaterial = null;
          this.clearSelectedUI("filterMaterial");
        }
      }

      if (step === "heatPump") {
        if (value !== "zodiac") {
          this.state.heatPumpVariantId = null;
        }

        if (value === "none") {
          this.state.heatPumpVariantId = null;
          this.state.heatPumpDistance = "";

          const input =
            this.root.querySelector(
              '[data-state-input="heatPumpDistance"]'
            );

          if (input) input.value = "";
        }
      }

      if (step === "summerCover" && value === "no") {
        this.state.reel = null;
        this.clearSelectedUI("reel");
      }

      if (step === "size") {
        if (
          ["2x4", "2x5"].includes(value) &&
          this.state.counterCurrent === "yes"
        ) {
          this.state.counterCurrent = null;
          this.clearSelectedUI("counterCurrent");
        }

        if (
          ["4x8", "4x9", "5x10"].includes(value) &&
          this.state.preassembled === "techBoxSmall"
        ) {
          this.state.preassembled = null;
          this.clearSelectedUI("preassembled");
        }
      }
    }

    clearSelectedUI(step) {
      this.root
        .querySelectorAll(
          `[data-option][data-step="${step}"]`
        )
        .forEach((button) => {
          button.classList.remove("is-selected");
          button.setAttribute("aria-pressed", "false");
        });
    }

    /* ======================================================
       AVAILABILITY
       ====================================================== */

    updatePreassembledAvailability() {
      const box =
        this.root.querySelector(
          '[data-option][data-step="preassembled"][data-value="techBoxSmall"]'
        );

      if (!box) return;

      const allowedSizes = [
        "2x4",
        "2x5",
        "3x5",
        "3x6",
        "3x7"
      ];

      const hasSize = Boolean(this.state.size);

      const allowed =
        !hasSize ||
        allowedSizes.includes(this.state.size);

      box.disabled = !allowed;

      box.classList.toggle(
        "is-size-blocked",
        !allowed
      );

      const notice =
        box.querySelector("[data-size-limit]");

      if (notice) {
        notice.hidden = allowed;
      }

      if (
        !allowed &&
        this.state.preassembled === "techBoxSmall"
      ) {
        this.state.preassembled = null;
        this.clearSelectedUI("preassembled");
      }
    }

    updateCounterCurrentAvailability() {
      const yesButton =
        this.root.querySelector(
          '[data-option][data-step="counterCurrent"][data-value="yes"]'
        );

      const notice =
        this.root.querySelector(
          "[data-countercurrent-notice]"
        );

      if (!yesButton) return;

      const unavailable =
        ["2x4", "2x5"].includes(this.state.size);

      yesButton.disabled = unavailable;

      if (notice) {
        if (unavailable) {
          notice.hidden = false;
          notice.textContent =
            "Die Gegenstromanlage ist beim DIVA erst ab 3 m Poolbreite möglich.";
        } else {
          notice.hidden = true;
          notice.textContent = "";
        }
      }

      if (
        unavailable &&
        this.state.counterCurrent === "yes"
      ) {
        this.state.counterCurrent = null;
        this.clearSelectedUI("counterCurrent");
      }
    }

    /* ======================================================
       PRODUCT LOADING
       ====================================================== */

    async loadProducts() {
      const entries =
        Object.entries(this.routes);

      await Promise.all(
        entries.map(async ([key, route]) => {
          try {
            this.productData[key] =
              await this.fetchProduct(route);

            debug(
              "Loaded product",
              key,
              this.productData[key]
            );
          } catch (error) {
            this.productErrors[key] = true;

            console.warn(
              `[DIVA] Product could not be loaded: ${key}`,
              error
            );
          }
        })
      );
    }

    async fetchProduct(route) {
      if (!route) {
        throw new Error("Product route missing");
      }

      if (this.productCache.has(route)) {
        return this.productCache.get(route);
      }

      const clean =
        route.endsWith(".js")
          ? route
          : `${route}.js`;

      const promise =
        fetch(clean, {
          credentials: "same-origin",
          headers: {
            Accept: "application/json"
          }
        }).then(async (response) => {
          if (!response.ok) {
            throw new Error(
              `${response.status} ${response.statusText}`
            );
          }

          return response.json();
        });

      this.productCache.set(route, promise);

      return promise;
    }

    /* ======================================================
       PRODUCT / VARIANT HELPERS
       ====================================================== */

    variantText(variant) {
      if (!variant) return "";

      return normalize(
        [
          variant.title,
          variant.option1,
          variant.option2,
          variant.option3
        ]
          .filter(Boolean)
          .join(" ")
      );
    }

    sizeTokens(size) {
      if (!size) return [];

      const [w, l] = size.split("x");

      return [
        normalize(size),
        normalize(`${w} x ${l}`),
        normalize(`${w},00 x ${l},00`),
        normalize(`${w}.00 x ${l}.00`),
        normalize(`${w}m x ${l}m`),
        normalize(`${w} m x ${l} m`)
      ];
    }

    variantMatchesSize(variant, size) {
      if (!variant || !size) return false;

      const text = this.variantText(variant);

      return this.sizeTokens(size).some((token) =>
        text.includes(token)
      );
    }

    availableVariants(product) {
      if (!product?.variants) return [];

      return product.variants.filter(
        (variant) => variant.available !== false
      );
    }

    findSizeVariant(product, size) {
      if (!product || !size) return null;

      const variants =
        this.availableVariants(product);

      return (
        variants.find((variant) =>
          this.variantMatchesSize(variant, size)
        ) || null
      );
    }

    colorMatches(text, color) {
      if (!color) return true;

      const n = normalize(text);

      if (color === "white") {
        return (
          n.includes("white") ||
          n.includes("weiss")
        );
      }

      if (color === "gray") {
        return (
          n.includes("gray") ||
          n.includes("grau")
        );
      }

      if (color === "beige") {
        return n.includes("beige");
      }

      return false;
    }

    findDivaVariant() {
      const product =
        this.productData.diva;

      if (!product || !this.state.size) {
        return null;
      }

      const sizeVariants =
        this.availableVariants(product).filter(
          (variant) =>
            this.variantMatchesSize(
              variant,
              this.state.size
            )
        );

      if (!sizeVariants.length) {
        debug(
          "No DIVA size variant",
          this.state.size
        );

        return null;
      }

      if (
        !this.state.rattan ||
        !this.state.liner
      ) {
        return null;
      }

      const exact =
        sizeVariants.find((variant) => {
          const text =
            [
              variant.title,
              variant.option1,
              variant.option2,
              variant.option3
            ]
              .filter(Boolean)
              .join(" ");

          return (
            this.colorMatches(
              text,
              this.state.rattan
            ) &&
            this.colorMatches(
              text,
              this.state.liner
            )
          );
        });

      if (exact) {
        debug(
          "DIVA exact variant",
          exact.id,
          exact.title,
          exact.price
        );

        return exact;
      }

      debug(
        "No exact DIVA color variant",
        {
          size: this.state.size,
          rattan: this.state.rattan,
          liner: this.state.liner,
          variants: sizeVariants.map((v) => v.title)
        }
      );

      return null;
    }

    getRepresentativeDivaPrice(size) {
      const product =
        this.productData.diva;

      if (!product || !size) return null;

      const variants =
        this.availableVariants(product).filter(
          (variant) =>
            this.variantMatchesSize(variant, size)
        );

      if (!variants.length) return null;

      const prices =
        variants
          .map((variant) =>
            Number(variant.price)
          )
          .filter(Number.isFinite);

      if (!prices.length) return null;

      return Math.min(...prices);
    }

    getProductImage(product, variant = null) {
      const candidates = [
        variant?.featured_image?.src,
        variant?.featured_image,
        product?.featured_image?.src,
        product?.featured_image,
        product?.images?.[0]?.src,
        product?.images?.[0]
      ];

      let image =
        candidates.find(
          (value) =>
            typeof value === "string" &&
            value.trim()
        ) || "";

      if (image.startsWith("//")) {
        image = `https:${image}`;
      }

      return image;
    }

    findVariantById(product, id) {
      if (!product || !id) return null;

      return (
        product.variants?.find(
          (variant) =>
            String(variant.id) === String(id)
        ) || null
      );
    }

    getDefaultVariant(product) {
      if (!product) return null;

      return (
        this.availableVariants(product)[0] ||
        product.variants?.[0] ||
        null
      );
    }

    /* ======================================================
       PRICE RESOLUTION
       ====================================================== */

    resolveAllPrices() {
      this.priceState.items = {};

      const divaVariant =
        this.findDivaVariant();

      this.priceState.divaVariant =
        divaVariant;

      if (divaVariant) {
        this.priceState.diva =
          Number(divaVariant.price);

        this.priceState.divaResolved =
          Number.isFinite(
            this.priceState.diva
          );
      } else {
        this.priceState.diva = null;
        this.priceState.divaResolved = false;
      }

      this.resolveSelectedProductPrices();
    }

    resolveSelectedProductPrices() {
      const add = (
        key,
        productKey,
        options = {}
      ) => {
        const product =
          this.productData[productKey];

        if (!product) {
          this.priceState.items[key] = {
            resolved: false,
            price: null,
            productKey,
            variant: null
          };

          return;
        }

        let variant = null;

        if (options.variantId) {
          variant =
            this.findVariantById(
              product,
              options.variantId
            );
        } else if (options.sizeDependent) {
          variant =
            this.findSizeVariant(
              product,
              this.state.size
            );
        } else {
          variant =
            this.getDefaultVariant(product);
        }

        if (!variant) {
          this.priceState.items[key] = {
            resolved: false,
            price: null,
            productKey,
            variant: null
          };

          return;
        }

        const basePrice =
          Number(variant.price);

        const quantity =
          options.quantity || 1;

        const price =
          Number.isFinite(basePrice)
            ? basePrice * quantity
            : null;

        this.priceState.items[key] = {
          resolved: Number.isFinite(price),
          price,
          unitPrice: basePrice,
          quantity,
          productKey,
          variant
        };
      };

      /* INDIVIDUAL HOUSING */

      if (
        this.state.technologyRoute === "individual" &&
        this.state.housing === "largeBox"
      ) {
        add(
          "housing",
          "techBoxLarge"
        );
      }

      /* INDIVIDUAL PUMP */

      if (
        this.state.technologyRoute === "individual" &&
        this.state.pump &&
        this.state.pump !== "standard"
      ) {
        const map = {
          baduPrime: "baduPrime",
          aquagem: "aquagem",
          baduDelta: "baduDelta",
          smartPlus: "smartPlus"
        };

        add(
          "pump",
          map[this.state.pump]
        );
      }

      /* INDIVIDUAL FILTER */

      if (
        this.state.technologyRoute === "individual" &&
        this.state.filter === "midaApollo"
      ) {
        add(
          "filter",
          "midaApollo"
        );
      }

      /* Cartridge is deliberately zero surcharge. */

      if (
        this.state.technologyRoute === "individual" &&
        this.state.filter === "standard" &&
        this.state.filterMaterial === "filterGlass"
      ) {
        add(
          "filterMaterial",
          "filterGlass",
          { quantity: 4 }
        );
      }

      if (
        this.state.technologyRoute === "individual" &&
        this.state.dosing === "bayrol"
      ) {
        add(
          "dosing",
          "bayrol"
        );
      }

      /* PREASSEMBLED */

      if (
        this.state.technologyRoute === "preassembled" &&
        this.state.preassembled
      ) {
        const map = {
          techBoxSmall: "techBoxSmall",
          techMedium: "techMedium",
          orbit: "orbit",
          combi: "combi"
        };

        add(
          "preassembled",
          map[this.state.preassembled]
        );
      }

      /* HEAT PUMP */

      if (this.state.heatPump === "cosma") {
        add(
          "heatPump",
          "cosma"
        );
      }

      if (this.state.heatPump === "zodiac") {
        add(
          "heatPump",
          "zodiac",
          {
            variantId:
              this.state.heatPumpVariantId
          }
        );
      }

      /* COMFORT */

      if (
        this.state.counterCurrent === "yes"
      ) {
        add(
          "counterCurrent",
          "counterCurrent"
        );
      }

      if (
        this.state.hydromassage === "yes"
      ) {
        add(
          "hydromassage",
          "hydromassage"
        );
      }

      /* GROUND */

      if (this.state.ground === "steel") {
        add(
          "ground",
          "steelFoundation",
          { sizeDependent: true }
        );
      }

      if (
        this.state.adjustment === "yes"
      ) {
        add(
          "adjustment",
          "adjustment",
          { sizeDependent: true }
        );
      }

      /* COVERS */

      if (
        this.state.summerCover === "yes"
      ) {
        add(
          "summerCover",
          "summerCover",
          { sizeDependent: true }
        );
      }

      if (
        this.state.summerCover === "yes" &&
        this.state.reel === "yes"
      ) {
        add(
          "reel",
          "reel",
          { sizeDependent: true }
        );
      }

      if (
        this.state.winterCover === "yes"
      ) {
        add(
          "winterCover",
          "winterCover",
          { sizeDependent: true }
        );
      }

      if (
        this.state.plexiglass === "yes"
      ) {
        add(
          "plexiglass",
          "plexiglass"
        );
      }
    }

    calculateTotal() {
      if (
        !this.state.size ||
        !this.state.rattan ||
        !this.state.liner
      ) {
        return {
          resolved: false,
          incomplete: true,
          total: null
        };
      }

      if (!this.priceState.divaResolved) {
        return {
          resolved: false,
          incomplete: false,
          total: null
        };
      }

      let total =
        this.priceState.diva;

      for (const item of Object.values(
        this.priceState.items
      )) {
        if (!item.resolved) {
          return {
            resolved: false,
            incomplete: false,
            total: null
          };
        }

        total += item.price;
      }

      return {
        resolved: true,
        incomplete: false,
        total
      };
    }

    money(cents) {
      if (!Number.isFinite(cents)) {
        return "Preis auf Anfrage";
      }

      return new Intl.NumberFormat(
        "de-DE",
        {
          style: "currency",
          currency: this.currency
        }
      ).format(cents / 100);
    }

    /* ======================================================
       PRODUCT CARD UI
       ====================================================== */

    updateProductCards() {
      this.root
        .querySelectorAll("[data-product-key]")
        .forEach((card) => {
          const key =
            card.dataset.productKey;

          const product =
            this.productData[key];

          const title =
            card.querySelector(
              "[data-product-title]"
            );

          const image =
            card.querySelector(
              "[data-product-image]"
            );

          if (product) {
            if (title && product.title) {
              title.textContent =
                product.title;
            }

            let variant = null;

            if (
              card.hasAttribute(
                "data-size-dependent"
              )
            ) {
              variant =
                this.findSizeVariant(
                  product,
                  this.state.size
                );
            } else if (
              key === "zodiac" &&
              this.state.heatPumpVariantId
            ) {
              variant =
                this.findVariantById(
                  product,
                  this.state.heatPumpVariantId
                );
            } else {
              variant =
                this.getDefaultVariant(product);
            }

            const src =
              this.getProductImage(
                product,
                variant
              );

            if (image && src) {
              image.src = src;
            }
          }

          this.updateCardPrice(card);
        });

      this.updateSizePrices();
    }

    updateCardPrice(card) {
      const key =
        card.dataset.productKey;

      const priceNode =
        card.querySelector(
          "[data-product-price]"
        );

      if (!priceNode) return;

      if (
        card.dataset.priceMode ===
        "replacement-free"
      ) {
        priceNode.textContent =
          "0 € Aufpreis · im Austausch ohne Aufpreis";

        return;
      }

      const product =
        this.productData[key];

      if (!product) {
        priceNode.textContent =
          "Preis auf Anfrage";

        return;
      }

      let variant = null;

      if (
        card.hasAttribute(
          "data-size-dependent"
        )
      ) {
        if (!this.state.size) {
          priceNode.textContent =
            "Größe zuerst wählen";

          return;
        }

        variant =
          this.findSizeVariant(
            product,
            this.state.size
          );
      } else if (
        key === "zodiac"
      ) {
        if (!this.state.heatPumpVariantId) {
          priceNode.textContent =
            "Variante wählen";

          return;
        }

        variant =
          this.findVariantById(
            product,
            this.state.heatPumpVariantId
          );
      } else {
        variant =
          this.getDefaultVariant(product);
      }

      if (!variant) {
        priceNode.textContent =
          "Preis auf Anfrage";

        return;
      }

      const quantity =
        Number(card.dataset.quantity || 1);

      const unit =
        Number(variant.price);

      if (!Number.isFinite(unit)) {
        priceNode.textContent =
          "Preis auf Anfrage";

        return;
      }

      const total =
        unit * quantity;

      priceNode.textContent =
        this.money(total);

      const quantityDetail =
        card.querySelector(
          "[data-quantity-price-detail]"
        );

      if (
        quantityDetail &&
        quantity > 1
      ) {
        quantityDetail.textContent =
          `${quantity} × ${this.money(unit)}`;
      }
    }

    updateSizePrices() {
      this.root
        .querySelectorAll("[data-size-price]")
        .forEach((node) => {
          const size =
            node.dataset.sizePrice;

          const price =
            this.getRepresentativeDivaPrice(size);

          if (price === null) {
            node.textContent =
              "Preis auf Anfrage";
          } else {
            node.textContent =
              `ab ${this.money(price)}`;
          }
        });
    }

    /* ======================================================
       ZODIAC VARIANTS
       ====================================================== */

    populateZodiacVariants() {
      const select =
        this.root.querySelector(
          "[data-variant-select]"
        );

      const product =
        this.productData.zodiac;

      if (!select || !product) return;

      const variants =
        this.availableVariants(product);

      select.innerHTML = "";

      const placeholder =
        document.createElement("option");

      placeholder.value = "";
      placeholder.textContent =
        "Bitte Ausführung wählen";

      select.appendChild(placeholder);

      variants.forEach((variant) => {
        const option =
          document.createElement("option");

        option.value =
          String(variant.id);

        option.textContent =
          `${variant.title} · ${this.money(
            Number(variant.price)
          )}`;

        select.appendChild(option);
      });

      if (this.state.heatPumpVariantId) {
        select.value =
          String(
            this.state.heatPumpVariantId
          );
      }
    }

    /* ======================================================
       MEASUREMENTS
       ====================================================== */

    getFootprint() {
      if (!this.state.size) return null;

      const parts =
        this.state.size
          .split("x")
          .map(Number);

      if (
        parts.length !== 2 ||
        parts.some((n) => !Number.isFinite(n))
      ) {
        return null;
      }

      return {
        width: parts[0] + 0.4,
        length: parts[1] + 0.4
      };
    }

    numberDE(value) {
      return Number(value)
        .toFixed(2)
        .replace(".", ",");
    }

    updateMeasurements() {
      const footprint =
        this.getFootprint();

      const footprintNodes =
        this.root.querySelectorAll(
          "[data-footprint-output]"
        );

      const preparedNodes =
        this.root.querySelectorAll(
          "[data-prepared-ground-output]"
        );

      const sizeInfo =
        this.root.querySelector(
          "[data-size-info]"
        );

      if (!footprint) {
        footprintNodes.forEach(
          (node) =>
            (node.textContent = "–")
        );

        preparedNodes.forEach(
          (node) =>
            (node.textContent = "–")
        );

        if (sizeInfo) {
          sizeInfo.textContent =
            "Wählen Sie eine Größe, um Aufstellmaß und aktuellen Preis zu sehen.";
        }

        return;
      }

      const footprintText =
        `${this.numberDE(
          footprint.width
        )} × ${this.numberDE(
          footprint.length
        )} m`;

      footprintNodes.forEach(
        (node) =>
          (node.textContent =
            footprintText)
      );

      let preparedText = "–";

      if (
        this.state.ground === "concrete" ||
        this.state.ground === "slabs"
      ) {
        preparedText =
          `ca. ${this.numberDE(
            footprint.width + 0.6
          )} × ${this.numberDE(
            footprint.length + 0.6
          )} m`;
      }

      if (
        this.state.ground === "steel"
      ) {
        preparedText =
          `${footprintText} Fundamentmaß`;
      }

      preparedNodes.forEach(
        (node) =>
          (node.textContent =
            preparedText)
      );

      if (sizeInfo) {
        sizeInfo.innerHTML =
          `<strong>${SIZE_LABELS[this.state.size]}</strong> Wasserfläche · tatsächliches DIVA Aufstellmaß <strong>${footprintText}</strong>.`;
      }
    }

    updateGroundDetails() {
      this.root
        .querySelectorAll(
          "[data-ground-detail]"
        )
        .forEach((detail) => {
          detail.hidden =
            detail.dataset.groundDetail !==
            this.state.ground;
        });
    }

    /* ======================================================
       CONTEXT NOTICES
       ====================================================== */

    updateContextualNotices() {
      const cartridge =
        this.root.querySelector(
          "[data-cartridge-notice]"
        );

      if (cartridge) {
        cartridge.hidden =
          !(
            this.state.technologyRoute ===
              "individual" &&
            this.state.filter ===
              "cartridge"
          );
      }

      const backwash =
        this.root.querySelector(
          "[data-backwash-result]"
        );

      if (backwash) {
        backwash.hidden = true;
        backwash.textContent = "";

        if (
          this.state.technologyRoute ===
            "preassembled" &&
          this.state.backwash === "no"
        ) {
          backwash.hidden = false;

          backwash.textContent =
            "Für diese fertig verrohrte Techniklösung ist eine Kartuschenfilter-Ausführung erforderlich. Der Austausch erfolgt ohne Aufpreis.";
        }

        if (
          this.state.technologyRoute ===
            "preassembled" &&
          this.state.backwash === "unknown"
        ) {
          backwash.hidden = false;

          backwash.textContent =
            "POOL24 prüft mit Ihnen, ob am geplanten Technikstandort eine geeignete Rückspülmöglichkeit vorhanden ist.";
        }

        if (
          this.state.technologyRoute ===
            "individual" &&
          this.state.backwash === "no"
        ) {
          backwash.hidden = false;

          backwash.textContent =
            "Für die gewählte Sandfilterlösung ist eine geeignete Ableitung des Rückspülwassers erforderlich. POOL24 prüft mit Ihnen die passende Ausführung.";
        }
      }
    }

    /* ======================================================
       TOTAL
       ====================================================== */

    updateTotal() {
      const result =
        this.calculateTotal();

      let text =
        "Poolgröße wählen";

      if (
        this.state.size &&
        (
          !this.state.rattan ||
          !this.state.liner
        )
      ) {
        const representative =
          this.getRepresentativeDivaPrice(
            this.state.size
          );

        text =
          representative !== null
            ? `ab ${this.money(
                representative
              )}`
            : "Preis auf Anfrage";
      }

      if (
        this.state.size &&
        this.state.rattan &&
        this.state.liner &&
        !result.resolved
      ) {
        text =
          "Preis auf Anfrage";
      }

      if (result.resolved) {
        text =
          this.money(result.total);
      }

      this.root
        .querySelectorAll(
          "[data-total-price]"
        )
        .forEach((node) => {
          node.textContent = text;
        });

      this.updateConfigurationPayload();
    }

    /* ======================================================
       PREVIEW
       ====================================================== */

    updatePreview() {
      const image =
        this.root.querySelector(
          "[data-preview-image]"
        );

      const label =
        this.root.querySelector(
          "[data-preview-label]"
        );

      const title =
        this.root.querySelector(
          "[data-preview-title]"
        );

      const text =
        this.root.querySelector(
          "[data-preview-text]"
        );

      const chips =
        this.root.querySelector(
          "[data-selection-chips]"
        );

      if (image) {
        image.src = this.getPreviewImage();
      }

      if (label) {
        label.textContent =
          this.state.size
            ? `Dolce Vita DIVA · ${SIZE_LABELS[this.state.size]}`
            : "Dolce Vita DIVA";
      }

      if (title) {
        if (!this.state.size) {
          title.textContent =
            "Ihr Sommer beginnt hier.";
        } else if (
          !this.state.rattan ||
          !this.state.liner
        ) {
          title.textContent =
            `DIVA ${SIZE_LABELS[this.state.size]}`;
        } else {
          title.textContent =
            "Ihr persönlicher DIVA.";
        }
      }

      if (text) {
        const footprint =
          this.getFootprint();

        if (!this.state.size) {
          text.textContent =
            "Stellen Sie Ihren persönlichen DIVA Schritt für Schritt zusammen.";
        } else if (footprint) {
          text.textContent =
            `Wasserfläche ${SIZE_LABELS[this.state.size]}, Aufstellmaß ca. ${this.numberDE(
              footprint.width
            )} × ${this.numberDE(
              footprint.length
            )} m.`;
        }
      }

      if (chips) {
        chips.innerHTML = "";

        const values = [];

        if (this.state.size) {
          values.push(
            SIZE_LABELS[this.state.size]
          );
        }

        if (this.state.rattan) {
          values.push(
            LABELS.rattan[
              this.state.rattan
            ]
          );
        }

        if (this.state.liner) {
          values.push(
            LABELS.liner[
              this.state.liner
            ]
          );
        }

        if (this.state.technologyRoute) {
          values.push(
            LABELS.technologyRoute[
              this.state.technologyRoute
            ]
          );
        }

        if (
          this.state.heatPump &&
          this.state.heatPump !== "none"
        ) {
          values.push(
            LABELS.heatPump[
              this.state.heatPump
            ]
          );
        }

        if (
          this.state.counterCurrent === "yes"
        ) {
          values.push("Gegenstrom");
        }

        if (
          this.state.hydromassage === "yes"
        ) {
          values.push("Hydromassage");
        }

        values.forEach((value) => {
          const chip =
            document.createElement("span");

          chip.textContent = value;

          chips.appendChild(chip);
        });
      }
    }

    getPreviewImage() {
      if (
        this.state.liner === "beige"
      ) {
        return "https://cdn.shopify.com/s/files/1/0885/9031/9956/files/Cremefarbene_Vinylfolie_mit_gefalteter_Ecke.png?v=1791409770";
      }

      if (
        this.state.rattan === "white"
      ) {
        return "https://cdn.shopify.com/s/files/1/0885/9031/9956/files/Rattan_Verkleidung.webp?v=1791408924";
      }

      if (
        this.state.rattan === "gray"
      ) {
        return "https://cdn.shopify.com/s/files/1/0885/9031/9956/files/detPLAYApannellatura03-768x768.webp?v=1791408926";
      }

      return this.heroImage;
    }

    /* ======================================================
       SUMMARY
       ====================================================== */

    updateSummary() {
      const container =
        this.root.querySelector(
          "[data-summary]"
        );

      if (!container) return;

      const rows = [];

      const addRow = (
        title,
        detail,
        price = null
      ) => {
        rows.push({
          title,
          detail,
          price
        });
      };

      if (this.state.size) {
        const footprint =
          this.getFootprint();

        const detail =
          footprint
            ? `${SIZE_LABELS[this.state.size]} Wasserfläche · Aufstellmaß ${this.numberDE(
                footprint.width
              )} × ${this.numberDE(
                footprint.length
              )} m`
            : SIZE_LABELS[this.state.size];

        addRow(
          "Dolce Vita DIVA",
          detail,
          this.priceState.divaResolved
            ? this.money(
                this.priceState.diva
              )
            : "Preis auf Anfrage"
        );
      }

      if (this.state.rattan) {
        addRow(
          "Rattanfarbe",
          LABELS.rattan[
            this.state.rattan
          ],
          "im Poolpreis"
        );
      }

      if (this.state.liner) {
        addRow(
          "Poolfolie",
          LABELS.liner[
            this.state.liner
          ],
          "im Poolpreis"
        );
      }

      addRow(
        "Serienausstattung",
        "AstralPool Sena, Cantabric Sandfilter, Luxury Edelstahlleiter AISI 316, LED, Skimmer, Reinigungsset, Überlauf und Standard-Anschlussmaterial",
        "enthalten"
      );

      if (
        this.state.technologyRoute ===
        "individual"
      ) {
        addRow(
          "Technikausführung",
          "Individuell zusammengestellte Pooltechnik",
          null
        );

        if (this.state.housing) {
          addRow(
            "Technik-Unterbringung",
            LABELS.housing[
              this.state.housing
            ],
            this.itemPriceText("housing")
          );
        }

        if (this.state.pump) {
          addRow(
            "Poolpumpe",
            LABELS.pump[
              this.state.pump
            ],
            this.state.pump === "standard"
              ? "enthalten"
              : this.itemPriceText("pump")
          );
        }

        if (this.state.filter) {
          let filterPrice = null;

          if (
            this.state.filter === "standard"
          ) {
            filterPrice = "enthalten";
          } else if (
            this.state.filter === "cartridge"
          ) {
            filterPrice =
              "Austausch ohne Aufpreis";
          } else {
            filterPrice =
              this.itemPriceText("filter");
          }

          addRow(
            "Filter",
            LABELS.filter[
              this.state.filter
            ],
            filterPrice
          );
        }

        if (
          this.state.filter === "standard" &&
          this.state.filterMaterial
        ) {
          addRow(
            "Filtermaterial",
            LABELS.filterMaterial[
              this.state.filterMaterial
            ],
            this.state.filterMaterial ===
            "filterGlass"
              ? this.itemPriceText(
                  "filterMaterial"
                )
              : "bauseits"
          );
        }

        if (this.state.dosing) {
          addRow(
            "Wasserpflege",
            LABELS.dosing[
              this.state.dosing
            ],
            this.state.dosing === "none"
              ? "0 €"
              : this.itemPriceText(
                  "dosing"
                )
          );
        }
      }

      if (
        this.state.technologyRoute ===
        "preassembled"
      ) {
        addRow(
          "Technikausführung",
          "Fertig verrohrte Techniklösung",
          null
        );

        if (this.state.preassembled) {
          addRow(
            "Techniklösung",
            LABELS.preassembled[
              this.state.preassembled
            ],
            this.itemPriceText(
              "preassembled"
            )
          );
        }

        if (
          this.state.backwash === "no"
        ) {
          addRow(
            "Filterausführung",
            "Kartuschenfilter-Ausführung erforderlich",
            "ohne Aufpreis"
          );
        }
      }

      if (this.state.backwash) {
        addRow(
          "Rückspülung",
          LABELS.backwash[
            this.state.backwash
          ],
          null
        );
      }

      if (
        this.state.technologyDistance
      ) {
        addRow(
          "Pool → Technik",
          `ca. ${this.state.technologyDistance} m`,
          "Planungsangabe"
        );
      }

      if (this.state.heatPump) {
        let detail =
          LABELS.heatPump[
            this.state.heatPump
          ];

        if (
          this.state.heatPump === "zodiac" &&
          this.state.heatPumpVariantId
        ) {
          const variant =
            this.findVariantById(
              this.productData.zodiac,
              this.state.heatPumpVariantId
            );

          if (variant) {
            detail += ` · ${variant.title}`;
          }
        }

        addRow(
          "Wärmepumpe",
          detail,
          this.state.heatPump === "none"
            ? "0 €"
            : this.itemPriceText(
                "heatPump"
              )
        );
      }

      if (
        this.state.heatPumpDistance &&
        this.state.heatPump !== "none"
      ) {
        addRow(
          "Technik → Wärmepumpe",
          `ca. ${this.state.heatPumpDistance} m`,
          "Planungsangabe"
        );
      }

      if (this.state.counterCurrent) {
        addRow(
          "Gegenstromanlage",
          LABELS.counterCurrent[
            this.state.counterCurrent
          ],
          this.state.counterCurrent === "yes"
            ? this.itemPriceText(
                "counterCurrent"
              )
            : "0 €"
        );
      }

      if (this.state.hydromassage) {
        addRow(
          "Hydromassage",
          LABELS.hydromassage[
            this.state.hydromassage
          ],
          this.state.hydromassage === "yes"
            ? this.itemPriceText(
                "hydromassage"
              )
            : "0 €"
        );
      }

      if (this.state.ground) {
        addRow(
          "Untergrund",
          LABELS.ground[
            this.state.ground
          ],
          this.state.ground === "steel"
            ? this.itemPriceText("ground")
            : "bauseits / individuell"
        );
      }

      if (this.state.adjustment) {
        addRow(
          "Justageelemente",
          LABELS.adjustment[
            this.state.adjustment
          ],
          this.state.adjustment === "yes"
            ? this.itemPriceText(
                "adjustment"
              )
            : "0 €"
        );
      }

      if (this.state.summerCover) {
        addRow(
          "Sommerabdeckung",
          LABELS.summerCover[
            this.state.summerCover
          ],
          this.state.summerCover === "yes"
            ? this.itemPriceText(
                "summerCover"
              )
            : "0 €"
        );
      }

      if (
        this.state.summerCover === "yes" &&
        this.state.reel
      ) {
        addRow(
          "Aufrollvorrichtung",
          LABELS.reel[
            this.state.reel
          ],
          this.state.reel === "yes"
            ? this.itemPriceText("reel")
            : "0 €"
        );
      }

      if (this.state.winterCover) {
        addRow(
          "Winterabdeckung",
          LABELS.winterCover[
            this.state.winterCover
          ],
          this.state.winterCover === "yes"
            ? this.itemPriceText(
                "winterCover"
              )
            : "0 €"
        );
      }

      if (this.state.plexiglass) {
        addRow(
          "Leiter / Sicherheit",
          LABELS.plexiglass[
            this.state.plexiglass
          ],
          this.state.plexiglass === "yes"
            ? this.itemPriceText(
                "plexiglass"
              )
            : "0 €"
        );
      }

      if (!rows.length) {
        container.innerHTML =
          '<div class="dx-summary-loading">Beginnen Sie mit der Auswahl Ihrer Poolgröße.</div>';

        this.updateConfigurationPayload();

        return;
      }

      container.innerHTML =
        rows
          .map((row) => {
            return `
              <div class="dx-summary-row">
                <div>
                  <div class="dx-summary-row__title">${this.escapeHTML(
                    row.title
                  )}</div>

                  ${
                    row.detail
                      ? `<span class="dx-summary-row__detail">${this.escapeHTML(
                          row.detail
                        )}</span>`
                      : ""
                  }
                </div>

                ${
                  row.price
                    ? `<div class="dx-summary-row__price">${this.escapeHTML(
                        row.price
                      )}</div>`
                    : ""
                }
              </div>
            `;
          })
          .join("");

      this.updateConfigurationPayload();
    }

    itemPriceText(key) {
      const item =
        this.priceState.items[key];

      if (!item) {
        return "Preis auf Anfrage";
      }

      if (!item.resolved) {
        return "Preis auf Anfrage";
      }

      return this.money(item.price);
    }

    /* ======================================================
       FORM PAYLOAD
       ====================================================== */

    updateConfigurationPayload() {
      const field =
        this.root.querySelector(
          "[data-configuration-payload]"
        );

      if (!field) return;

      const result =
        this.calculateTotal();

      const lines = [
        "DIVA KONFIGURATION",
        "=================="
      ];

      if (this.state.size) {
        lines.push(
          `Poolgröße: ${SIZE_LABELS[this.state.size]}`
        );

        const footprint =
          this.getFootprint();

        if (footprint) {
          lines.push(
            `Aufstellmaß: ${this.numberDE(
              footprint.width
            )} × ${this.numberDE(
              footprint.length
            )} m`
          );
        }
      }

      if (this.state.rattan) {
        lines.push(
          `Rattan: ${LABELS.rattan[this.state.rattan]}`
        );
      }

      if (this.state.liner) {
        lines.push(
          `Folie: ${LABELS.liner[this.state.liner]}`
        );
      }

      if (this.state.technologyRoute) {
        lines.push(
          `Technikausführung: ${
            LABELS.technologyRoute[
              this.state.technologyRoute
            ]
          }`
        );
      }

      if (
        this.state.technologyRoute ===
        "individual"
      ) {
        if (this.state.housing) {
          lines.push(
            `Technik-Unterbringung: ${
              LABELS.housing[
                this.state.housing
              ]
            }`
          );
        }

        if (this.state.pump) {
          lines.push(
            `Pumpe: ${
              LABELS.pump[
                this.state.pump
              ]
            }`
          );
        }

        if (this.state.filter) {
          lines.push(
            `Filter: ${
              LABELS.filter[
                this.state.filter
              ]
            }`
          );
        }

        if (this.state.filterMaterial) {
          lines.push(
            `Filtermaterial: ${
              LABELS.filterMaterial[
                this.state.filterMaterial
              ]
            }`
          );
        }

        if (this.state.dosing) {
          lines.push(
            `Dosierung: ${
              LABELS.dosing[
                this.state.dosing
              ]
            }`
          );
        }
      }

      if (
        this.state.technologyRoute ===
          "preassembled" &&
        this.state.preassembled
      ) {
        lines.push(
          `Fertig verrohrte Technik: ${
            LABELS.preassembled[
              this.state.preassembled
            ]
          }`
        );
      }

      if (this.state.backwash) {
        lines.push(
          `Rückspülung: ${
            LABELS.backwash[
              this.state.backwash
            ]
          }`
        );
      }

      if (
        this.state.technologyRoute ===
          "preassembled" &&
        this.state.backwash === "no"
      ) {
        lines.push(
          "Ausführung: Kartuschenfilter erforderlich – ohne Aufpreis"
        );
      }

      if (this.state.technologyDistance) {
        lines.push(
          `Entfernung Pool → Technik: ca. ${this.state.technologyDistance} m`
        );
      }

      if (this.state.heatPump) {
        let hp =
          LABELS.heatPump[
            this.state.heatPump
          ];

        if (
          this.state.heatPump === "zodiac" &&
          this.state.heatPumpVariantId
        ) {
          const variant =
            this.findVariantById(
              this.productData.zodiac,
              this.state.heatPumpVariantId
            );

          if (variant) {
            hp += ` · ${variant.title}`;
          }
        }

        lines.push(
          `Wärmepumpe: ${hp}`
        );
      }

      if (this.state.heatPumpDistance) {
        lines.push(
          `Entfernung Technik → Wärmepumpe: ca. ${this.state.heatPumpDistance} m`
        );
      }

      if (this.state.counterCurrent) {
        lines.push(
          `Gegenstrom: ${
            LABELS.counterCurrent[
              this.state.counterCurrent
            ]
          }`
        );
      }

      if (this.state.hydromassage) {
        lines.push(
          `Hydromassage: ${
            LABELS.hydromassage[
              this.state.hydromassage
            ]
          }`
        );
      }

      if (this.state.ground) {
        lines.push(
          `Untergrund: ${
            LABELS.ground[
              this.state.ground
            ]
          }`
        );
      }

      if (this.state.adjustment) {
        lines.push(
          `Justageelemente: ${
            LABELS.adjustment[
              this.state.adjustment
            ]
          }`
        );
      }

      if (this.state.summerCover) {
        lines.push(
          `Sommerabdeckung: ${
            LABELS.summerCover[
              this.state.summerCover
            ]
          }`
        );
      }

      if (
        this.state.summerCover === "yes" &&
        this.state.reel
      ) {
        lines.push(
          `Aufrollvorrichtung: ${
            LABELS.reel[
              this.state.reel
            ]
          }`
        );
      }

      if (this.state.winterCover) {
        lines.push(
          `Winterabdeckung: ${
            LABELS.winterCover[
              this.state.winterCover
            ]
          }`
        );
      }

      if (this.state.plexiglass) {
        lines.push(
          `Plexiglas-Sicherheitskit: ${
            LABELS.plexiglass[
              this.state.plexiglass
            ]
          }`
        );
      }

      lines.push("");
      lines.push(
        result.resolved
          ? `Konfigurationspreis: ${this.money(
              result.total
            )}`
          : "Konfigurationspreis: Preis auf Anfrage / noch nicht vollständig auflösbar"
      );

      lines.push("");
      lines.push(
        "Hinweis: Konfiguration technisch durch POOL24 prüfen."
      );

      field.value =
        lines.join("\n");
    }

    /* ======================================================
       HELPERS
       ====================================================== */

    escapeHTML(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }
  }

  /* ========================================================
     BOOTSTRAP
     ======================================================== */

  const boot = () => {
    document
      .querySelectorAll(
        "[data-diva-configurator]"
      )
      .forEach((root) => {
        if (
          root.dataset.divaInitialized ===
          "true"
        ) {
          return;
        }

        root.dataset.divaInitialized =
          "true";

        new DivaConfigurator(root);
      });
  };

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    boot
  );
})();